import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Betragsfeld, Button, Callout, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, toast } from '@elbklar/ui';

import { SelectField, TextField, TextareaField } from '@/components/Felder';
import { plannedApi } from '@/lib/api';
import { heuteIso } from '@/lib/format';
import { betragFehler, ohneVorzeichen } from '@/lib/betrag';
import { datum, ganzzahl, hatFehler, hoechstens, ohne, pflicht, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import { STEUERSAETZE, steuerHinweis } from '@/lib/steuer';
import type { Category, PlannedItem } from '@/types';

type Feld = 'name' | 'faellig' | 'betrag' | 'anzahl';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  kategorien: Category[];
  onGespeichert: () => void;
  /** Gesetzt = bearbeiten, leer = neu planen. */
  posten?: PlannedItem | null;
}

const LEER = {
  name: '',
  richtung: 'aus' as 'aus' | 'ein',
  /** Decimal-Text aus dem Betragsfeld, `null` solange keine Zahl dasteht. */
  betrag: '' as string | null,
  faellig: heuteIso(),
  kategorie: '',
  steuersatz: '0',
  wiederholung: '',
  anzahl: '',
  notiz: '',
};

function ausPosten(posten: PlannedItem) {
  return {
    name: posten.name,
    richtung: (Number(posten.amount) < 0 ? 'aus' : 'ein') as 'aus' | 'ein',
    betrag: ohneVorzeichen(posten.amount) as string | null,
    faellig: posten.due_date,
    kategorie: posten.category_id ?? '',
    steuersatz: String(Number(posten.tax_rate ?? 0)),
    wiederholung: posten.repeat_interval ?? '',
    anzahl: posten.repeat_count ? String(posten.repeat_count) : '',
    notiz: posten.notes ?? '',
  };
}

export function PlannedItemDialog({
  offen,
  onSchliessen,
  kategorien,
  onGespeichert,
  posten,
}: Props) {
  const [formular, setFormular] = useState(LEER);
  const [fehler, setFehler] = useState<Fehler<Feld>>({});

  const bearbeiten = Boolean(posten);
  const istFest = posten?.status === 'committed';

  useEffect(() => {
    if (!offen) return;
    setFormular(posten ? ausPosten(posten) : LEER);
    setFehler({});
  }, [offen, posten]);

  /** Wert setzen und den Fehler des Felds zurücknehmen. */
  const setze = (feld: Feld, wert: string) => {
    setFormular({ ...formular, [feld]: wert });
    setFehler((f) => ohne(f, feld));
  };

  const speichern = useMutation({
    mutationFn: () => {
      // Decimal-Text aus dem Betragsfeld, von `absenden` geprüft; nie als
      // Gleitkommazahl (vorher kam hier `-${Number}` an).
      const betrag = ohneVorzeichen(formular.betrag ?? '');
      const nutzdaten = {
        name: formular.name.trim(),
        // Die Richtung traegt das Vorzeichen; das Modell erwartet Ausgaben negativ.
        amount: formular.richtung === 'aus' ? `-${betrag}` : betrag,
        due_date: formular.faellig,
        category_id: formular.kategorie || null,
        tax_rate: formular.steuersatz,
        repeat_interval: formular.wiederholung || null,
        // Ohne Wiederholung keine Anzahl: das Feld ist dann gesperrt, kann aber
        // noch eine Zahl von vorher tragen, und die API weist die Anzahl
        // ohne Rhythmus ab.
        repeat_count: formular.wiederholung && formular.anzahl ? Number(formular.anzahl) : null,
        notes: formular.notiz.trim() || null,
      };

      return posten
        ? plannedApi.update(posten.id, nutzdaten)
        : plannedApi.create(nutzdaten);
    },
    onSuccess: () => {
      toast.success(
        bearbeiten ? 'Posten geändert' : 'Als Entwurf angelegt, bereit zum Durchrechnen'
      );
      if (!bearbeiten) setFormular(LEER);
      onGespeichert();
      onSchliessen();
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  // Grenzen aus PlannedItemCreate (finance-api): Name 1–255 Zeichen, Anzahl
  // 1–600 und nur zusammen mit einem Rhythmus.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const unlesbar = unlesbareFelder(event.currentTarget);
    const neu: Fehler<Feld> = {
      name: pflicht(formular.name, 'Angabe „Wofür“ fehlt') ?? hoechstens(formular.name, 255),
      faellig: datum(formular.faellig, { unlesbar: unlesbar.has('faellig') }),
      betrag: betragFehler(formular.betrag, { nichtNull: true }),
      anzahl: formular.wiederholung
        ? ganzzahl(formular.anzahl, { min: 1, max: 600, pflicht: true, unlesbar: unlesbar.has('anzahl') })
        : undefined,
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onSchliessen()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{bearbeiten ? 'Geplanten Posten bearbeiten' : 'Ausgabe planen'}</DialogTitle>
        </DialogHeader>
        <form noValidate onSubmit={absenden} className="space-y-4">
          <TextField
            label="Wofür"
            placeholder="z. B. Transporter"
            value={formular.name}
            onChange={(e) => setze('name', e.target.value)}
            error={fehler.name}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label="Richtung"
              options={[
                { value: 'aus', label: 'Ausgabe' },
                { value: 'ein', label: 'Einnahme' },
              ]}
              value={formular.richtung}
              onChange={(e) =>
                setFormular({
                  ...formular,
                  richtung: e.target.value as 'aus' | 'ein',
                })
              }
            />
            <TextField
              label="Fällig am"
              type="date"
              name="faellig"
              value={formular.faellig}
              onChange={(e) => setze('faellig', e.target.value)}
              error={fehler.faellig}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Betragsfeld
              label="Betrag brutto (€)"
              placeholder="28000"
              value={formular.betrag ?? ''}
              onChange={(betrag) => {
                setFormular({ ...formular, betrag });
                setFehler((f) => ohne(f, 'betrag'));
              }}
              hinweis={(wert) => steuerHinweis(wert, formular.steuersatz, 'Ohne Vorzeichen, rechnen erlaubt: 28000*1,19')}
              error={fehler.betrag}
              required
            />
            <SelectField
              label="Umsatzsteuer"
              options={STEUERSAETZE}
              value={formular.steuersatz}
              onChange={(e) =>
                setFormular({ ...formular, steuersatz: e.target.value })
              }
            />
          </div>

          <SelectField
            label="Kategorie"
            options={[{ value: '', label: 'ohne Kategorie' }, ...kategorien.map((k) => ({ value: k.id, label: k.name }))]}
            value={formular.kategorie}
            onChange={(e) => setFormular({ ...formular, kategorie: e.target.value })}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label="Wiederholung"
              options={[
                { value: '', label: 'einmalig' },
                { value: 'monthly', label: 'monatlich' },
                { value: 'quarterly', label: 'quartalsweise' },
                { value: 'yearly', label: 'jährlich' },
              ]}
              value={formular.wiederholung}
              onChange={(e) => {
                setFormular({ ...formular, wiederholung: e.target.value });
                setFehler((f) => ohne(f, 'anzahl'));
              }}
            />
            <TextField
              label="Wie oft"
              type="number"
              name="anzahl"
              min={1}
              max={600}
              placeholder="z. B. 12"
              disabled={!formular.wiederholung}
              value={formular.anzahl}
              onChange={(e) => setze('anzahl', e.target.value)}
              error={fehler.anzahl}
              required={Boolean(formular.wiederholung)}
            />
          </div>

          <TextareaField
            label="Notiz"
            rows={2}
            value={formular.notiz}
            onChange={(e) => setFormular({ ...formular, notiz: e.target.value })}
          />

          {!bearbeiten && (
            <p className="rounded-lg bg-muted px-3 py-2 t-small text-muted-foreground">
              Der Posten wird als <strong className="font-medium text-foreground">Entwurf</strong> angelegt und wirkt
              zunächst nur in der Simulation. Erst „Fest einplanen“ macht ihn dauerhaft wirksam.
            </p>
          )}

          {istFest && (
            <Callout tone="warning" icon={TriangleAlert} title="Fest eingeplant" boxed>
              Die Änderung wirkt sofort in der Basis-Projektion, nicht nur in der Simulation.
            </Callout>
          )}

          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onSchliessen}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" loading={speichern.isPending}>
              {bearbeiten ? 'Posten speichern' : 'Entwurf anlegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
